import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'copyVoterListCheckbox',
    'cantBeCopiedHelpText',
    'isTestRadio',
    'errorMessage',
    'toggleButton',
    'singleDuplicateElements',
    'singleDuplicateButtons',
    'multipleDuplicateElements',
    'multipleDuplicateButtons',
    'multipleDuplicateButtonsTable'
  ];

  declare copyVoterListCheckboxTarget: HTMLInputElement;
  declare cantBeCopiedHelpTextTarget: HTMLElement;
  declare isTestRadioTarget: HTMLInputElement;
  declare errorMessageTarget: HTMLElement;
  declare toggleButtonTarget: HTMLElement;

  declare singleDuplicateElementsTarget: HTMLElement;
  declare singleDuplicateButtonsTarget: HTMLElement;

  declare multipleDuplicateElementsTarget: HTMLElement;
  declare multipleDuplicateButtonsTarget: HTMLElement;
  declare multipleDuplicateButtonsTableTarget: HTMLElement;

  private voterListUnusableForTest!: boolean;
  private previousCopyVoterListCheckboxValue?: boolean;
  private mode: 'single' | 'multiple' = 'single';
  private isDirty = false;

  connect() {
    this.voterListUnusableForTest =
      parseInt((this.element as HTMLElement).dataset.sourceVoterListLength!, 10) > 5;

    if (this.voterListUnusableForTest && this.isTestRadioTarget.checked) {
      this.copyVoterListCheckboxTarget.checked = false;
      this.copyVoterListCheckboxTarget.disabled = true;
    }

    const form = this.element.querySelector('form');
    if (!form) return;

    form.querySelectorAll('input, textarea, select').forEach((el) => {
      el.addEventListener('input', () => {
        this.isDirty = true;
      });
    });

    const parentDiv = document.getElementById('multiple_duplicate_component');
    if (parentDiv) {
      parentDiv.addEventListener('handsontable-changed', (e: any) => {
        this.isDirty = true;
      });
    }

    form.addEventListener('submit', () => {
      this.isDirty = false;
    });

    window.addEventListener('beforeunload', (e) => {
      if (!this.isDirty) return;
      e.preventDefault();
    });
  }

  updateForm(event: Event) {
    if (!this.voterListUnusableForTest) return;

    const target = event.target as HTMLInputElement;
    const isTest = target.checked && target.value === 'true';

    if (isTest) {
      this.previousCopyVoterListCheckboxValue = this.copyVoterListCheckboxTarget.checked;
      this.copyVoterListCheckboxTarget.checked = false;
      this.copyVoterListCheckboxTarget.disabled = true;
      this.cantBeCopiedHelpTextTarget.classList.remove('hidden');
    } else {
      this.copyVoterListCheckboxTarget.checked =
        this.previousCopyVoterListCheckboxValue ?? false;
      this.copyVoterListCheckboxTarget.disabled = false;
      this.cantBeCopiedHelpTextTarget.classList.add('hidden');
    }
  }

  toggleMode() {
    if (this.mode === 'single') {
      this.mode = 'multiple';
      this.showMultipleMode();
    } else {
      this.mode = 'single';
      this.showSingleMode();
    }
  }

  handleError(event: any) {
    const [data, _status, _xhr] = event.detail;

    this.errorMessageTarget.innerText = '';
    this.errorMessageTarget.classList.add('hidden');

    if (!data.errors || data.errors.length === 0) return;

    const tableErrors: { row?: number; cols?: number[]; errors: string[] }[] = [];
    const repeatErrorMessages = new Set<string>();
    const timerRelatedKeywords = /end date|countdown timer|voting duration/i;

    data.errors.forEach((error: { row?: number; cols?: number[]; errors: string[] }) => {
      const relevantMessages = error.errors.filter(msg =>
        timerRelatedKeywords.test(msg)
      );

      const otherMessages = error.errors.filter(msg =>
        !timerRelatedKeywords.test(msg)
      );

      if (otherMessages.length > 0) {
        tableErrors.push({ ...error, errors: otherMessages });
      }

      relevantMessages.forEach(msg => repeatErrorMessages.add(msg));
    });

    tableErrors.forEach(error => {
      if (typeof error.row === 'number' && Array.isArray(error.cols)) {
        const errorCells = error.cols.map(col => ({ row: error.row!, col }));
        this.sendHighlightMultiple(errorCells);
      }
    });

    const formattedTableErrors = tableErrors
      .map(error => {
        const messages = error.errors.join(' || ');
        return typeof error.row === 'number'
          ? `Row ${error.row + 1}: ${messages}`
          : messages;
      })
      .filter(Boolean)
      .join('\n');

    const uniqueRepeatErrors = Array.from(repeatErrorMessages).join('\n');

    const allMessages = [formattedTableErrors, uniqueRepeatErrors]
      .filter(Boolean)
      .join('\n');

    if (allMessages) {
      this.errorMessageTarget.innerText =
        `Please fix the following errors:\n${allMessages}`;
      this.errorMessageTarget.classList.remove('hidden');
    }
  }


  sendHighlightMultiple(cells: { row: number, col: number }[]) {
    const tableElement = document.getElementById('duplicate-table')
    if (!tableElement) return

    const event = new CustomEvent('highlight-multiple-cells', {
      detail: { cells }
    })
    tableElement.dispatchEvent(event)
  }

  fillFirstRow(data: { [key: string]: string }) {
    const tableElement = document.getElementById('duplicate-table');
    if (!tableElement) return;

    const event = new CustomEvent('fill-first-row', {
      detail: { firstRow: data }
    });
    tableElement.dispatchEvent(event);
  }

  private showSingleMode() {
    this.multipleDuplicateElementsTarget.classList.add('hidden-table');
    this.multipleDuplicateButtonsTarget.classList.add('hidden');
    this.multipleDuplicateButtonsTableTarget.classList.add('hidden');

    this.singleDuplicateElementsTarget.classList.remove('hidden');
    this.singleDuplicateButtonsTarget.classList.remove('hidden');

    this.toggleButtonTarget.textContent = 'Multiple Motions';

    const duplicateFormElement = document.getElementById('duplicate_form') as HTMLFormElement;
    if (duplicateFormElement) duplicateFormElement.action = '/votes/duplicate';

    const hotWrapper = document.getElementById('multipleDuplicateTable') as HTMLElement;
    if (hotWrapper) hotWrapper.style.display = 'none';

    this.isDirty = false;
  }

  private showMultipleMode() {
    this.singleDuplicateElementsTarget.classList.add('hidden');
    this.singleDuplicateButtonsTarget.classList.add('hidden');

    this.multipleDuplicateElementsTarget.classList.remove('hidden-table');
    this.multipleDuplicateButtonsTarget.classList.remove('hidden');
    this.multipleDuplicateButtonsTableTarget.classList.remove('hidden');

    this.toggleButtonTarget.textContent = 'Single Motion';
    this.toggleButtonTarget.classList.add('hidden');

    const duplicateFormElement = document.getElementById('duplicate_form') as HTMLFormElement;
    if (duplicateFormElement) duplicateFormElement.action = '/votes/multiple_duplicate';

    const hotWrapper = document.getElementById('multipleDuplicateTable') as HTMLElement;
    if (hotWrapper) hotWrapper.style.display = '';

    this.isDirty = false;

    const keyField = document.getElementById('key_field') as HTMLInputElement;
    const voteTitleField = document.getElementById('vote_title_field') as HTMLInputElement;
    const ballotQuestionField = document.getElementById('ballot_question_field') as HTMLInputElement;

    const firstRowData = {
      vote_key: keyField ? keyField.value : '',
      vote_title: voteTitleField ? voteTitleField.value : '',
      ballot_question_title: ballotQuestionField ? ballotQuestionField.value : ''
    };

    this.fillFirstRow(firstRowData);
  }
}
